'use client'
import React, { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { updateSearchParams, removeParams, removeSearchParam } from '@utils'
import { FaMapMarkerAlt, FaList } from 'react-icons/fa'
import { AiOutlineMenuFold } from 'react-icons/ai'
import { categoryTypes } from '@constants/homeCategoryTypes'
import { homeRoomTypes } from '@constants/homeRoomTypes'
import { saleMaxPrices } from '@constants/saleMaxPrice'
import { saleMinPrices } from '@constants/saleMinPrice'
import { rentMaxPrices } from '@constants/rentMaxPrice'
import { rentMinPrices } from '@constants/rentMinPrice'

// ─── RTL ──────────────────────────────────────────────────────────────────────

const RTL_LANGS = new Set(['he', 'ar'])

// ─── GA4 Helpers ──────────────────────────────────────────────────────────────

function readCurrentFilters() {
  if (typeof window === 'undefined') return {}
  const p = new URLSearchParams(window.location.search)

  const mergeParam = (singular, plural) => {
    const a = p.get(singular)?.split(',').filter(Boolean) ?? []
    const b = p.get(plural)?.split(',').filter(Boolean)   ?? []
    const merged = [...new Set([...a, ...b])]
    return merged.length ? merged.join(',') : null
  }

  const categories = mergeParam('category', 'categories')
  const districts  = mergeParam('district', 'districts')
  const provinces  = mergeParam('province', 'provinces')
  const bts        = p.get('bts') || null
  const amenities  = p.get('amenities') || null
  const tags       = p.get('tags') || null

  return {
    search_sale_type:        p.get('saleType')     || 'sale',
    search_categories:       categories || 'all',
    search_districts:        districts || 'all',
    search_districts_count:  districts?.split(',').filter(Boolean).length ?? 0,
    search_provinces:        provinces || 'all',
    search_provinces_count:  provinces?.split(',').filter(Boolean).length ?? 0,
    search_bts:              bts || 'all',
    search_bts_count:        bts?.split(',').filter(Boolean).length        ?? 0,
    search_amenities:        amenities || 'all',
    search_amenities_count:  amenities?.split(',').filter(Boolean).length  ?? 0,
    search_tags:             tags || 'all',
    search_tags_count:       tags?.split(',').filter(Boolean).length       ?? 0,
    search_bedrooms:         p.get('room')         || null,
    search_min_price:        p.get('minprice')     || null,
    search_max_price:        p.get('maxprice')     || null,
    search_min_area:         p.get('minlivingarea')      || null,
    search_max_area:         p.get('maxlivingarea')      || null,
    search_name_query:       p.get('name')         || null,
    search_location:         p.get('location')     || null,
    search_pet_friendly:     p.get('petfriendly')  || null,
    search_sea_view:         p.get('seaview')      || null,
    search_furnished:        p.get('furnished')    || null,
    search_foreign_quota:    p.get('foreignquota') || null,
    search_mode:             p.get('map') === 'true' ? 'map' : 'list',
    search_filter_count: [
      categories,
      districts,
      provinces,
      bts,
      amenities,
      tags,
      p.get('room'),
      p.get('minprice'),
      p.get('maxprice'),
      p.get('minlivingarea'),
      p.get('maxlivingarea'),
      p.get('name'),
      p.get('location'),
      p.get('petfriendly'),
      p.get('seaview'),
      p.get('furnished'),
      p.get('foreignquota'),
    ].filter(Boolean).length,
  }
}

async function fireImmediateGAEvent(eventName, params = {}) {
  try {
    const { sendGAEvent } = await import('@next/third-parties/google')
    sendGAEvent('event', eventName, params)
  } catch (e) {
    console.error('GA event error:', e)
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SLOT_LABEL_DEFAULTS = {
  saleType:          { en: 'Sale Type',       th: 'ประเภท',           cn: '类型',      ru: 'Тип',               fr: 'Type',            de: 'Typ',              it: 'Tipo',            ja: '種別',          ko: '유형',          es: 'Tipo',            tr: 'Satış Tipi',      he: 'סוג עסקה',        ar: 'نوع البيع'        },
  category:          { en: 'Category',        th: 'หมวดหมู่',          cn: '类别',      ru: 'Категория',          fr: 'Catégorie',       de: 'Kategorie',        it: 'Categoria',       ja: 'カテゴリー',     ko: '카테고리',       es: 'Categoría',       tr: 'Kategori',        he: 'קטגוריה',         ar: 'الفئة'            },
  categories:        { en: 'Categories',      th: 'หมวดหมู่',          cn: '类别',      ru: 'Категории',          fr: 'Catégories',      de: 'Kategorien',       it: 'Categorie',       ja: 'カテゴリー',     ko: '카테고리',       es: 'Categorías',      tr: 'Kategoriler',     he: 'קטגוריות',        ar: 'الفئات'           },
  district:          { en: 'District',        th: 'เขต',               cn: '区域',      ru: 'Район',              fr: 'Quartier',        de: 'Bezirk',           it: 'Quartiere',       ja: '地区',          ko: '지역',          es: 'Distrito',        tr: 'Bölge',           he: 'רובע',            ar: 'الحي'             },
  districts:         { en: 'Districts',       th: 'เขต',               cn: '区域',      ru: 'Районы',             fr: 'Quartiers',       de: 'Bezirke',          it: 'Quartieri',       ja: '地区',          ko: '지역',          es: 'Distritos',       tr: 'Bölgeler',        he: 'רובעים',          ar: 'الأحياء'          },
  province:          { en: 'Province',        th: 'จังหวัด',           cn: '省份',      ru: 'Провинция',         fr: 'Province',        de: 'Provinz',          it: 'Provincia',       ja: '県',            ko: '지역(도)',      es: 'Provincia',       tr: 'İl',              he: 'מחוז',            ar: 'المحافظة'         },
  provinces:         { en: 'Provinces',       th: 'จังหวัด',           cn: '省份',      ru: 'Провинции',         fr: 'Provinces',       de: 'Provinzen',        it: 'Province',        ja: '県',            ko: '지역(도)',      es: 'Provincias',      tr: 'İller',           he: 'מחוזות',          ar: 'المحافظات'        },
  bts:               { en: 'BTS / MRT',       th: 'BTS / MRT',         cn: 'BTS / MRT', ru: 'BTS / MRT',         fr: 'BTS / MRT',       de: 'BTS / MRT',        it: 'BTS / MRT',       ja: 'BTS / MRT',     ko: 'BTS / MRT',     es: 'BTS / MRT',       tr: 'Metro / Tramvay', he: 'BTS / MRT',        ar: 'BTS / MRT'        },
  amenities:         { en: 'Amenities',       th: 'สิ่งอำนวยความสะดวก', cn: '设施',      ru: 'Удобства',           fr: 'Commodités',      de: 'Ausstattung',      it: 'Servizi',         ja: '設備',          ko: '편의시설',       es: 'Comodidades',     tr: 'Olanaklar',       he: 'שירותים',         ar: 'المرافق'          },
  tags:              { en: 'Tags',            th: 'แท็ก',              cn: '标签',      ru: 'Теги',               fr: 'Étiquettes',      de: 'Tags',             it: 'Tag',             ja: 'タグ',           ko: '태그',           es: 'Etiquetas',       tr: 'Etiketler',       he: 'תגיות',           ar: 'الوسوم'           },
  bedrooms:          { en: 'Bedrooms',        th: 'ห้องนอน',            cn: '卧室',      ru: 'Спальни',            fr: 'Chambres',        de: 'Schlafzimmer',     it: 'Camere',          ja: '寝室',          ko: '침실',          es: 'Habitaciones',    tr: 'Yatak Odası',     he: 'חדרי שינה',       ar: 'غرف النوم'        },
  rooms:             { en: 'Bedrooms',        th: 'ห้องนอน',            cn: '卧室',      ru: 'Спальни',            fr: 'Chambres',        de: 'Schlafzimmer',     it: 'Camere',          ja: '寝室',          ko: '침실',          es: 'Habitaciones',    tr: 'Odalar',          he: 'חדרי שינה',       ar: 'غرف النوم'        },
  bathrooms:         { en: 'Bathrooms',       th: 'ห้องน้ำ',            cn: '浴室',      ru: 'Ванные',             fr: 'Salles de bain',  de: 'Badezimmer',       it: 'Bagni',           ja: 'バスルーム',     ko: '욕실',          es: 'Baños',           tr: 'Banyolar',        he: 'חדרי אמבטיה',     ar: 'الحمامات'         },
  minPrice:          { en: 'Min Price',       th: 'ราคาต่ำสุด',         cn: '最低价格',   ru: 'Цена от',            fr: 'Prix min',        de: 'Mindestpreis',     it: 'Prezzo min',      ja: '最低価格',       ko: '최저가',         es: 'Precio mín.',     tr: 'Min. Fiyat',      he: 'מחיר מינימום',    ar: 'أدنى سعر'         },
  maxPrice:          { en: 'Max Price',       th: 'ราคาสูงสุด',         cn: '最高价格',   ru: 'Цена до',            fr: 'Prix max',        de: 'Höchstpreis',      it: 'Prezzo max',      ja: '最高価格',       ko: '최고가',         es: 'Precio máx.',     tr: 'Max. Fiyat',      he: 'מחיר מקסימום',    ar: 'أقصى سعر'         },
  minRentPrice:      { en: 'Min Rent',        th: 'เช่าต่ำสุด',         cn: '最低租金',   ru: 'Аренда от',          fr: 'Loyer min',       de: 'Mindestmiete',     it: 'Affitto min',     ja: '最低賃料',       ko: '최저 임대료',     es: 'Alquiler mín.',   tr: 'Min. Kira',       he: 'שכירות מינימום',  ar: 'أدنى إيجار'       },
  maxRentPrice:      { en: 'Max Rent',        th: 'เช่าสูงสุด',         cn: '最高租金',   ru: 'Аренда до',          fr: 'Loyer max',       de: 'Höchstmiete',      it: 'Affitto max',     ja: '最高賃料',       ko: '최고 임대료',     es: 'Alquiler máx.',   tr: 'Max. Kira',       he: 'שכירות מקסימום',  ar: 'أقصى إيجار'       },
  minLivingArea:     { en: 'Min Area (sqm)',  th: 'พื้นที่ต่ำสุด (ตร.ม.)', cn: '最小面积 (平方米)', ru: 'Площадь от (м²)', fr: 'Surface min (m²)', de: 'Min. Fläche (m²)', it: 'Area min (m²)', ja: '最小面積 (m²)', ko: '최소 면적 (m²)', es: 'Área mín. (m²)', tr: 'Min. Alan (m²)', he: 'שטח מינימום (מ"ר)', ar: 'أدنى مساحة (م²)' },
  maxLivingArea:     { en: 'Max Area (sqm)',  th: 'พื้นที่สูงสุด (ตร.ม.)', cn: '最大面积 (平方米)', ru: 'Площадь до (м²)', fr: 'Surface max (m²)', de: 'Max. Fläche (m²)', it: 'Area max (m²)', ja: '最大面積 (m²)', ko: '최대 면적 (m²)', es: 'Área máx. (m²)', tr: 'Max. Alan (m²)', he: 'שטח מקסימום (מ"ר)', ar: 'أقصى مساحة (م²)' },
  sort:              { en: 'Sort By',         th: 'เรียงตาม',           cn: '排序',      ru: 'Сортировка',         fr: 'Trier par',       de: 'Sortieren',        it: 'Ordina per',      ja: '並び替え',       ko: '정렬',          es: 'Ordenar por',     tr: 'Sırala',          he: 'מיון לפי',        ar: 'ترتيب حسب'        },
  nameSearch:        { en: 'Name / Ref',      th: 'ชื่อ / รหัส',       cn: '名称 / 编号', ru: 'Имя / Код',          fr: 'Nom / Réf',       de: 'Name / Ref',       it: 'Nome / Rif',      ja: '名前 / 参照',    ko: '이름 / 참조',    es: 'Nombre / Ref.',   tr: 'İsim / Ref No',   he: 'שם / מזהה',       ar: 'الاسم / المرجع'   },
  locationSearch:    { en: 'Location',        th: 'สถานที่',            cn: '地点',      ru: 'Местоположение',     fr: 'Lieu',            de: 'Ort',              it: 'Luogo',           ja: '場所',          ko: '위치',          es: 'Ubicación',       tr: 'Konum',           he: 'מיקום',           ar: 'الموقع'           },
  areaSearch:        { en: 'Area',            th: 'พื้นที่',            cn: '区域',      ru: 'Район',              fr: 'Zone',            de: 'Gebiet',           it: 'Area',            ja: 'エリア',         ko: '지역',          es: 'Área',            tr: 'Alan',            he: 'אזור',            ar: 'المنطقة'          },
  petFriendly:       { en: 'Pet Friendly',    th: 'อนุญาตสัตว์เลี้ยง',  cn: '允许宠物',   ru: 'Можно с питомцами',  fr: 'Animaux admis',   de: 'Haustiere erlaubt',it: 'Animali ammessi', ja: 'ペット可',       ko: '반려동물 가능',   es: 'Admite mascotas', tr: 'Evcil Hayvan',    he: 'ידידותי לחיות',   ar: 'مناسب للحيوانات' },
  seaView:           { en: 'Sea View',        th: 'วิวทะเล',            cn: '海景',      ru: 'Вид на море',        fr: 'Vue mer',         de: 'Meerblick',        it: 'Vista mare',      ja: 'シービュー',     ko: '바다 전망',       es: 'Vista al mar',    tr: 'Deniz Manzaralı', he: 'נוף לים',         ar: 'إطلالة بحرية'     },
  beachfront:        { en: 'Beachfront',      th: 'ติดชายหาด',          cn: '海滨',      ru: 'Первая линия',       fr: 'Bord de mer',     de: 'Strandlage',       it: 'Fronte mare',     ja: 'ビーチフロント',  ko: '해변가',         es: 'Primera línea',   tr: 'Denize Sıfır',    he: 'חזית הים',        ar: 'واجهة الشاطئ'     },
  pool:              { en: 'Pool',            th: 'สระว่ายน้ำ',         cn: '游泳池',    ru: 'Бассейн',            fr: 'Piscine',         de: 'Pool',             it: 'Piscina',         ja: 'プール',         ko: '수영장',         es: 'Piscina',         tr: 'Havuz',           he: 'בריכה',           ar: 'مسبح'             },
  gym:               { en: 'Gym',             th: 'ฟิตเนส',            cn: '健身房',    ru: 'Тренажёрный зал',    fr: 'Salle de sport',  de: 'Fitnessstudio',    it: 'Palestra',        ja: 'ジム',           ko: '헬스장',         es: 'Gimnasio',        tr: 'Spor Salonu',     he: 'חדר כושר',        ar: 'صالة رياضية'      },
  parking:           { en: 'Parking',         th: 'ที่จอดรถ',          cn: '停车场',    ru: 'Парковка',           fr: 'Parking',         de: 'Parkplatz',        it: 'Parcheggio',      ja: '駐車場',         ko: '주차장',         es: 'Aparcamiento',    tr: 'Otopark',         he: 'חניה',            ar: 'موقف سيارات'      },
  furnished:         { en: 'Furnished',       th: 'มีเฟอร์นิเจอร์',     cn: '带家具',    ru: 'С мебелью',          fr: 'Meublé',          de: 'Möbliert',         it: 'Arredato',        ja: '家具付き',       ko: '가구 포함',       es: 'Amueblado',       tr: 'Eşyalı',          he: 'מרוהט',           ar: 'مفروش'            },
  foreignQuota:      { en: 'Foreign Quota',   th: 'โควต้าต่างชาติ',     cn: '外国配额',   ru: 'Иностранная квота',  fr: 'Quota étranger',  de: 'Ausländerkontingent', it: 'Quota stranieri', ja: '外国人枠',       ko: '외국인 쿼터',     es: 'Cuota extranjera',tr: 'Yabancı Kotası',  he: 'מכסה זרים',       ar: 'حصة الأجانب'      },
  mountainView:      { en: 'Mountain View',   th: 'วิวภูเขา',           cn: '山景',      ru: 'Вид на горы',        fr: 'Vue montagne',    de: 'Bergblick',        it: 'Vista montagna',  ja: '山の景色',       ko: '산 전망',         es: 'Vista montaña',   tr: 'Dağ Manzaralı',   he: 'נוף להרים',       ar: 'إطلالة جبلية'     },
  searchButton:      { en: 'Search',          th: 'ค้นหา',              cn: '搜索',      ru: 'Поиск',              fr: 'Rechercher',      de: 'Suchen',           it: 'Cerca',           ja: '検索',           ko: '검색',           es: 'Buscar',          tr: 'Ara',             he: 'חיפוש',           ar: 'بحث'              },
  resetButton:       { en: 'Reset',           th: 'รีเซ็ต',             cn: '重置',      ru: 'Сбросить',           fr: 'Réinitialiser',   de: 'Zurücksetzen',     it: 'Reimposta',       ja: 'リセット',       ko: '초기화',         es: 'Restablecer',     tr: 'Sıfırla',         he: 'איפוס',           ar: 'إعادة تعيين'      },
  mapToggle:         { en: 'Map View',        th: 'ดูแผนที่',           cn: '地图视图',   ru: 'Карта',              fr: 'Vue carte',       de: 'Kartenansicht',    it: 'Mappa',           ja: 'マップ表示',     ko: '지도 보기',       es: 'Ver mapa',        tr: 'Harita Görünümü', he: 'תצוגת מפה',        ar: 'عرض الخريطة'      },
  viewToggle:        { en: 'View',            th: 'มุมมอง',            cn: '视图',      ru: 'Вид',                fr: 'Vue',             de: 'Ansicht',          it: 'Vista',           ja: '表示',           ko: '보기',           es: 'Vista',           tr: 'Görünüm',         he: 'תצוגה',           ar: 'عرض'              },
  moreFiltersToggle: { en: 'More Filters',    th: 'ตัวกรองเพิ่มเติม',   cn: '更多筛选',   ru: 'Ещё фильтры',        fr: 'Plus de filtres', de: 'Mehr Filter',      it: 'Altri filtri',    ja: 'さらに絞り込む',  ko: '더 많은 필터',    es: 'Más filtros',     tr: 'Daha Fazla Filtre',he: 'סינון נוסף',      ar: 'مزيد من المرشحات'},
  propertyCount:     { en: '{count} properties found', th: 'พบ {count} รายการ', cn: '找到 {count} 个房产', ru: 'Найдено {count}', fr: '{count} biens trouvés', de: '{count} Immobilien', it: '{count} proprietà', ja: '{count}件', ko: '{count}건', es: '{count} propiedades', tr: '{count} ilan bulundu', he: 'נמצאו {count} נכסים', ar: 'تم العثور على {count} عقار' },
  listView:          { en: 'List',            th: 'รายการ',            cn: '列表',      ru: 'Список',             fr: 'Liste',           de: 'Liste',            it: 'Elenco',          ja: 'リスト',          ko: '목록',          es: 'Lista',           tr: 'Liste',           he: 'רשימה',           ar: 'قائمة'            },
}

function resolveLabel(slot, slotLabels, lang) {
  const override = slotLabels?.[slot]
  if (override) {
    if (typeof override === 'string') return override
    return override[lang] || override.en || override
  }
  const defaults = SLOT_LABEL_DEFAULTS[slot]
  if (defaults) return defaults[lang] || defaults.en || slot
  return slot
}

function resolveCountLabel(slot, slotLabels, lang, count) {
  const tpl = resolveLabel(slot, slotLabels, lang)
  return tpl.replace('{count}', String(count ?? ''))
}

// ─── Multilingual label resolution helper ─────────────────────────────────────
// Used for curated amenity/tag entries, whose `label` is an object keyed by
// language (as saved by the admin's CuratedOptionsEditor / AmenityOptionsEditor).
// Falls back through: requested lang -> English -> whatever language IS
// filled in -> the raw value itself, so a curated entry never renders blank
// just because an admin skipped one language or a new site language was
// added after the entry was created.

function resolveMultilingualLabel(labelObj, lang, fallbackValue) {
  if (!labelObj) return String(fallbackValue ?? '')
  if (typeof labelObj === 'string') return labelObj || String(fallbackValue ?? '')
  const direct = labelObj[lang]
  if (direct) return direct
  if (labelObj.en) return labelObj.en
  const firstFilled = Object.values(labelObj).find(v => v)
  return firstFilled || String(fallbackValue ?? '')
}

// ─── Curated-or-derived option list resolver (used by amenities / tags) ───────
// If `curated` (an admin-configured array of { value, label: {en, th, ...} })
// is provided, it wins — this lets an agency pick a subset/order/relabeling
// of amenities or tags to actually show in the filter dropdown. For amenities,
// `value` is the amenity's stringified Mongo id (matching what's stored on
// the Property document); for tags it's whatever string the admin typed.
// Otherwise falls back to the raw list fetched from the DB (Amenity docs, or
// a plain string/tag list), deriving value/label as best it can.

function resolveOptionList(curated, fullList, lang) {
  if (curated?.length) {
    return curated.map(o => ({
      value: String(o.value),
      label: resolveMultilingualLabel(o.label, lang, o.value),
    }))
  }
  return (fullList || []).map(item => {
    if (typeof item === 'string') return { value: item, label: item }
    const value =
      item.value ?? item._id ?? item.id ?? item.name
    const label =
      (item.amenityName && (item.amenityName[lang] || item.amenityName.en)) ||
      item.name || item.label || item.title || value
    return { value: String(value), label: String(label) }
  })
}

// ─── Province-scoped district filtering ────────────────────────────────────────
// When a province filter is active in the URL, narrow the district options
// down to districts belonging to that province, so the district dropdown
// doesn't show districts from provinces the user has already excluded.
// Requires each district entry to carry the raw province key on `location`
// (matching what /activeprovinces hands back as each option's `id`).

function getProvinceScopedDistrictList(districtList, searchParams) {
  const raw = searchParams?.provinces || searchParams?.province
  if (!raw) return districtList
  const activeProvinceIds = String(raw).split(',').filter(Boolean)
  if (!activeProvinceIds.length) return districtList
  return (districtList || []).filter(d => activeProvinceIds.includes(d.location))
}

function getParam(key) {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get(key)
}

// ─── Shared dropdown style builder ───────────────────────────────────────────

function buildDropdownStyle(fs, isRTL) {
  return {
    width: '100%',
    height: fs.height || '38px',
    background: fs.bg || '#fff',
    color: fs.textColor || '#111',
    border: `${fs.borderWidth || 1}px solid ${fs.borderColor || '#d1d5db'}`,
    borderRadius: fs.borderRadius || '6px',
    fontSize: fs.fontSize || '0.875rem',
    paddingLeft: '10px',
    paddingRight: '10px',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
    appearance: 'none',
    WebkitAppearance: 'none',
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left',
  }
}

// ─── Dropdown wrapper with chevron (native selects only) ─────────────────────

function DropdownWrap({ fs, children, isRTL }) {
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      {children}
      <span style={{
        position: 'absolute',
        [isRTL ? 'left' : 'right']: 10,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        fontSize: '0.65em',
        color: fs.textColor || '#111',
        opacity: 0.5,
      }}>▾</span>
    </div>
  )
}

// ─── Field label ──────────────────────────────────────────────────────────────

function FieldLabel({ label, fs, isRTL }) {
  if (!fs.showLabels) return null
  return (
    <span style={{
      display: 'block',
      fontSize: fs.labelSize || '0.75rem',
      fontWeight: fs.labelWeight || '600',
      color: fs.labelColor || '#374151',
      marginBottom: 4,
      textAlign: isRTL ? 'right' : 'left',
      direction: isRTL ? 'rtl' : 'ltr',
    }}>
      {label}
    </span>
  )
}

// ─── Generic select (native) ──────────────────────────────────────────────────

function GenericSelect({ paramKey, options, label, fs, router, placeholder, lang, onFilterChange }) {
  const isRTL = RTL_LANGS.has(lang)
  const [value, setValue] = useState(() => getParam(paramKey) || '')
  useEffect(() => { setValue(getParam(paramKey) || '') }, [paramKey])
  const handleChange = (e) => {
    const v = e.target.value
    setValue(v)
    const path = v ? updateSearchParams(paramKey, v) : removeSearchParam(paramKey)
    router.push(path)
    router.refresh()
    onFilterChange?.({ filter_name: paramKey, filter_value: v || null })
  }
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <FieldLabel label={label} fs={fs} isRTL={isRTL} />
      <DropdownWrap fs={fs} isRTL={isRTL}>
        <select
          value={value}
          onChange={handleChange}
          style={{
            ...buildDropdownStyle(fs, isRTL),
            paddingLeft:  isRTL ? '28px' : '10px',
            paddingRight: isRTL ? '10px' : '28px',
          }}
        >
          <option value="">{placeholder || label}</option>
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </DropdownWrap>
    </div>
  )
}

// ─── Custom searchable dropdown ───────────────────────────────────────────────

function SearchableSelect({ paramKey, options, label, fs, router, placeholder, lang, onFilterChange }) {
  const isRTL = RTL_LANGS.has(lang)
  const [value, setValue]   = useState(() => getParam(paramKey) || '')
  const [open, setOpen]     = useState(false)
  const [query, setQuery]   = useState('')
  const ref = useRef(null)

  useEffect(() => { setValue(getParam(paramKey) || '') }, [paramKey])
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filtered = options.filter(o => {
    const l = o.label != null ? String(o.label) : ''
    return l.toLowerCase().includes(query.toLowerCase())
  })
  const selected = options.find(o => o.value === value)

  const select = (o) => {
    setValue(o.value); setOpen(false); setQuery('')
    router.push(updateSearchParams(paramKey, o.value)); router.refresh()
    onFilterChange?.({ filter_name: paramKey, filter_value: o.value })
  }
  const clear = () => {
    setValue(''); setOpen(false); setQuery('')
    router.push(removeSearchParam(paramKey)); router.refresh()
    onFilterChange?.({ filter_name: paramKey, filter_value: null })
  }

  return (
    <div ref={ref} style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <FieldLabel label={label} fs={fs} isRTL={isRTL} />
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          ...buildDropdownStyle(fs, isRTL),
          display: 'flex', alignItems: 'center', flexDirection: 'row',
          justifyContent: 'space-between', height: fs.height || '38px',
          cursor: 'pointer', userSelect: 'none',
          paddingLeft: isRTL ? '8px' : '10px', paddingRight: isRTL ? '10px' : '8px',
        }}
      >
        {isRTL && (
          <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0, marginRight: 4 }}>
            {value && (
              <span onClick={(e) => { e.stopPropagation(); clear() }}
                style={{ opacity: 0.4, fontSize: '0.8em', padding: '0 2px', cursor: 'pointer' }}>✕</span>
            )}
            <span style={{ opacity: 0.5, fontSize: '0.65em' }}>▾</span>
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {selected ? selected.label : <span style={{ opacity: 0.5 }}>{placeholder || label}</span>}
        </span>
        {!isRTL && (
          <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0, marginLeft: 4 }}>
            {value && (
              <span onClick={(e) => { e.stopPropagation(); clear() }}
                style={{ opacity: 0.4, fontSize: '0.8em', padding: '0 2px', cursor: 'pointer' }}>✕</span>
            )}
            <span style={{ opacity: 0.5, fontSize: '0.65em' }}>▾</span>
          </span>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: fs.menuBg || '#fff', border: `1px solid ${fs.borderColor || '#d1d5db'}`,
          borderRadius: fs.borderRadius || '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 200, maxHeight: 260, display: 'flex', flexDirection: 'column',
          direction: isRTL ? 'rtl' : 'ltr',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: `1px solid ${fs.borderColor || '#e5e7eb'}` }}>
            <input
              autoFocus value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search…" dir={isRTL ? 'rtl' : 'ltr'}
              style={{
                width: '100%', border: 'none', outline: 'none',
                fontSize: fs.fontSize || '0.875rem', background: 'transparent',
                color: fs.menuTextColor || '#374', boxSizing: 'border-box',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 200 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 12px', color: '#aaa', fontSize: '0.8rem', textAlign: isRTL ? 'right' : 'left' }}>No results</div>
            )}
            {filtered.map(o => {
              const isSelected = o.value === value
              return (
                <div key={o.value} onClick={() => select(o)} style={{
                  padding: '8px 12px', cursor: 'pointer',
                  fontSize: fs.fontSize || '0.875rem',
                  background: isSelected ? (fs.selectedBg || '#eff6ff') : 'transparent',
                  color: isSelected ? (fs.selectedTextColor || '#1d4ed8') : (fs.menuTextColor || '#374'),
                  fontWeight: isSelected ? (fs.selectedFontWeight || '600') : 'normal',
                  display: 'flex', alignItems: 'center',
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                  justifyContent: 'space-between', textAlign: isRTL ? 'right' : 'left',
                }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = fs.hoverBg || '#f3f4f6' }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
                >
                  {isRTL && isSelected && (fs.selectedShowCheck ?? true) && (
                    <span style={{ fontSize: '0.8em', marginLeft: 6 }}>✓</span>
                  )}
                  <span style={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>{o.label}</span>
                  {!isRTL && isSelected && (fs.selectedShowCheck ?? true) && (
                    <span style={{ fontSize: '0.8em', marginLeft: 6 }}>✓</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Multi-select dropdown ────────────────────────────────────────────────────

function MultiSelect({ paramKey, options, label, fs, router, placeholder, lang, onFilterChange }) {
  const isRTL = RTL_LANGS.has(lang)
  const [values, setValues] = useState(() => { const v = getParam(paramKey); return v ? v.split(',') : [] })
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => { const v = getParam(paramKey); setValues(v ? v.split(',') : []) }, [paramKey])
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const toggle = (val) => {
    const next = values.includes(val) ? values.filter(v => v !== val) : [...values, val]
    setValues(next)
    const path = next.length ? updateSearchParams(paramKey, next.join(',')) : removeSearchParam(paramKey)
    router.push(path); router.refresh()
    onFilterChange?.({ filter_name: paramKey, filter_value: next.join(',') || null })
  }

  const displayLabel = values.length
    ? options.filter(o => values.includes(o.value)).map(o => o.label).join(', ')
    : null

  return (
    <div ref={ref} style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <FieldLabel label={label} fs={fs} isRTL={isRTL} />
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          ...buildDropdownStyle(fs, isRTL),
          display: 'flex', alignItems: 'center', flexDirection: 'row',
          justifyContent: 'space-between', height: fs.height || '38px',
          cursor: 'pointer', userSelect: 'none',
          paddingLeft: isRTL ? '8px' : '10px', paddingRight: isRTL ? '10px' : '8px',
        }}
      >
        {isRTL && (
          <span style={{ opacity: 0.5, fontSize: '0.65em', flexShrink: 0, marginRight: 4 }}>▾</span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {displayLabel
            ? <span>{displayLabel}</span>
            : <span style={{ opacity: 0.5 }}>{placeholder || label}</span>}
        </span>
        {!isRTL && (
          <span style={{ opacity: 0.5, fontSize: '0.65em', flexShrink: 0, marginLeft: 4 }}>▾</span>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: fs.menuBg || '#fff', border: `1px solid ${fs.borderColor || '#d1d5db'}`,
          borderRadius: fs.borderRadius || '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 200, maxHeight: 260, overflowY: 'auto', direction: isRTL ? 'rtl' : 'ltr',
        }}>
          {options.map(o => {
            const isSelected = values.includes(o.value)
            return (
              <div key={o.value} onClick={() => toggle(o.value)} style={{
                padding: '8px 12px', cursor: 'pointer',
                fontSize: fs.fontSize || '0.875rem',
                background: isSelected ? (fs.selectedBg || '#eff6ff') : 'transparent',
                color: isSelected ? (fs.selectedTextColor || '#1d4ed8') : (fs.menuTextColor || '#374'),
                fontWeight: isSelected ? (fs.selectedFontWeight || '600') : 'normal',
                display: 'flex', alignItems: 'center',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                justifyContent: 'space-between', textAlign: isRTL ? 'right' : 'left',
              }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = fs.hoverBg || '#f3f4f6' }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
              >
                {isRTL && isSelected && (fs.selectedShowCheck ?? true) && (
                  <span style={{ fontSize: '0.8em', marginLeft: 6 }}>✓</span>
                )}
                <span style={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>{o.label}</span>
                {!isRTL && isSelected && (fs.selectedShowCheck ?? true) && (
                  <span style={{ fontSize: '0.8em', marginLeft: 6 }}>✓</span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Free-text number input (prices + living area) ────────────────────────────
// Opens a small popover with a number input. Commits on Enter or blur.
// Displays the committed value formatted with toLocaleString + optional unit suffix.

function FreeTextNumberInput({ paramKey, label, fs, router, placeholder, lang, unit, options, onFilterChange }) {
  const isRTL    = RTL_LANGS.has(lang)
  const [value, setValue] = useState(() => getParam(paramKey) || '')
  const [open, setOpen]   = useState(false)
  const ref               = useRef(null)
  const inputRef          = useRef(null)

  useEffect(() => { setValue(getParam(paramKey) || '') }, [paramKey])
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus()
  }, [open])

  const commit = (raw) => {
    const clean = String(raw).replace(/[^\d]/g, '')
    setValue(clean)
    setOpen(false)
    const path = clean ? updateSearchParams(paramKey, clean) : removeSearchParam(paramKey)
    router.push(path)
    router.refresh()
    onFilterChange?.({ filter_name: paramKey, filter_value: clean || null })
  }

  const selectPreset = (o) => {
    setValue(o.value)
    setOpen(false)
    router.push(updateSearchParams(paramKey, o.value))
    router.refresh()
    onFilterChange?.({ filter_name: paramKey, filter_value: o.value })
  }

  const clear = (e) => {
    e.stopPropagation()
    commit('')
  }

  // Find matching preset label for display, fall back to formatted number
  const selectedPreset = options?.find(o => o.value === value)
  const displayValue = value
    ? (selectedPreset?.label || `${Number(value).toLocaleString()}${unit ? ' ' + unit : ''}`)
    : null

  return (
    <div ref={ref} style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <FieldLabel label={label} fs={fs} isRTL={isRTL} />

      {/* Trigger */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          ...buildDropdownStyle(fs, isRTL),
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          height: fs.height || '38px',
          cursor: 'pointer', userSelect: 'none',
          paddingLeft:  isRTL ? '8px'  : '10px',
          paddingRight: isRTL ? '10px' : '8px',
        }}
      >
        {isRTL && (
          <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0, marginRight: 4 }}>
            {value && <span onClick={clear} style={{ opacity: 0.4, fontSize: '0.8em', padding: '0 2px', cursor: 'pointer' }}>✕</span>}
            <span style={{ opacity: 0.5, fontSize: '0.65em' }}>▾</span>
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {displayValue
            ? <span>{displayValue}</span>
            : <span style={{ opacity: 0.5 }}>{placeholder || label}</span>
          }
        </span>
        {!isRTL && (
          <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0, marginLeft: 4 }}>
            {value && <span onClick={clear} style={{ opacity: 0.4, fontSize: '0.8em', padding: '0 2px', cursor: 'pointer' }}>✕</span>}
            <span style={{ opacity: 0.5, fontSize: '0.65em' }}>▾</span>
          </span>
        )}
      </div>

      {/* Popover */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)',
          left: 0, right: 0, zIndex: 200,
          background: fs.menuBg || '#fff',
          border: `1px solid ${fs.borderColor || '#d1d5db'}`,
          borderRadius: fs.borderRadius || '6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          direction: isRTL ? 'rtl' : 'ltr',
          display: 'flex', flexDirection: 'column',
          maxHeight: 300, overflow: 'hidden',
        }}>
          {/* Free-text input at top */}
          <div style={{ padding: '8px 8px 6px', borderBottom: options?.length ? `1px solid ${fs.borderColor || '#e5e7eb'}` : 'none', flexShrink: 0 }}>
            <input
              ref={inputRef}
              type="number"
              min="0"
              defaultValue={value}
              placeholder={placeholder || label}
              dir={isRTL ? 'rtl' : 'ltr'}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit(e.target.value)
                if (e.key === 'Escape') setOpen(false)
              }}
              onBlur={(e) => {
                // Only commit on blur if no preset was clicked (handled by selectPreset via mousedown)
                // Small delay lets mousedown on a preset fire first
                setTimeout(() => {
                  if (open) commit(e.target.value)
                }, 150)
              }}
              style={{
                width: '100%', boxSizing: 'border-box',
                border: `1px solid ${fs.borderColor || '#d1d5db'}`,
                borderRadius: fs.borderRadius || '6px',
                fontSize: fs.fontSize || '0.875rem',
                padding: '6px 10px', outline: 'none',
                background: fs.bg || '#fff',
                color: fs.textColor || '#111',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
            {unit && (
              <div style={{ textAlign: isRTL ? 'left' : 'right', fontSize: '0.72rem', color: '#9ca3af', marginTop: 4 }}>
                {unit}
              </div>
            )}
          </div>

          {/* Preset options list */}
          {options?.length > 0 && (
            <div style={{ overflowY: 'auto', maxHeight: 220 }}>
              {options.map(o => {
                const isSelected = o.value === value
                return (
                  <div
                    key={o.value}
                    onMouseDown={(e) => { e.preventDefault(); selectPreset(o) }}
                    style={{
                      padding: '8px 12px', cursor: 'pointer',
                      fontSize: fs.fontSize || '0.875rem',
                      background: isSelected ? (fs.selectedBg || '#eff6ff') : 'transparent',
                      color: isSelected ? (fs.selectedTextColor || '#1d4ed8') : (fs.menuTextColor || '#374151'),
                      fontWeight: isSelected ? (fs.selectedFontWeight || '600') : 'normal',
                      display: 'flex', alignItems: 'center',
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                      justifyContent: 'space-between',
                      textAlign: isRTL ? 'right' : 'left',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = fs.hoverBg || '#f3f4f6' }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
                  >
                    {isRTL && isSelected && (fs.selectedShowCheck ?? true) && <span style={{ fontSize: '0.8em', marginLeft: 6 }}>✓</span>}
                    <span style={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>{o.label}</span>
                    {!isRTL && isSelected && (fs.selectedShowCheck ?? true) && <span style={{ fontSize: '0.8em', marginLeft: 6 }}>✓</span>}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Toggle switch ────────────────────────────────────────────────────────────

function ToggleSwitch({ paramKey, label, sw, router, lang, onFilterChange }) {
  const isRTL = RTL_LANGS.has(lang)
  const [on, setOn] = useState(() => getParam(paramKey) === 'true')
  useEffect(() => { setOn(getParam(paramKey) === 'true') }, [paramKey])
  const toggle = () => {
    const next = !on; setOn(next)
    const path = next ? updateSearchParams(paramKey, 'true') : removeSearchParam(paramKey)
    router.push(path); router.refresh()
    onFilterChange?.({ filter_name: paramKey, filter_value: next ? 'true' : null })
  }
  const isLeft = sw.layout === 'label-left'
  const isTop  = sw.layout === 'label-top'
  const flexDir = isTop ? 'column' : (isRTL ? (isLeft ? 'row' : 'row-reverse') : (isLeft ? 'row-reverse' : 'row'))
  return (
    <div onClick={toggle} style={{
      display: 'flex', flexDirection: flexDir,
      alignItems: isTop ? 'flex-start' : 'center',
      gap: 6, cursor: 'pointer', userSelect: 'none', direction: isRTL ? 'rtl' : 'ltr',
    }}>
      <div style={{
        width: 36, height: 20, borderRadius: 10,
        background: on ? (sw.activeColor || '#3b82f6') : '#d1d5db',
        position: 'relative', flexShrink: 0, transition: 'background 0.2s',
      }}>
        <div style={{
          position: 'absolute', top: 2, left: on ? 18 : 2,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <span style={{ fontSize: sw.labelSize || '0.875rem', fontWeight: sw.labelWeight || 'normal', color: sw.labelColor || '#374151' }}>
        {label}
      </span>
    </div>
  )
}

// ─── Location / Area text input with icon ─────────────────────────────────────

function LocationInput({ paramKey, label, fs, router, placeholder, lang, onFilterChange }) {
  const isRTL = RTL_LANGS.has(lang)
  const [value, setValue] = useState(() => getParam(paramKey) || '')
  useEffect(() => { setValue(getParam(paramKey) || '') }, [paramKey])
  const handleChange = (e) => {
    const v = e.target.value; setValue(v)
    const path = v ? updateSearchParams(paramKey, v) : removeSearchParam(paramKey)
    router.push(path)
    onFilterChange?.({ filter_name: paramKey, filter_value: v || null })
  }
  const iconColor = fs.locationIconColor || '#6b7280'
  const iconSize  = parseInt(fs.locationIconSize) || 16
  return (
    <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <FieldLabel label={label} fs={fs} isRTL={isRTL} />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span style={{
          position: 'absolute', [isRTL ? 'right' : 'left']: 10,
          top: '50%', transform: 'translateY(-50%)',
          color: iconColor, pointerEvents: 'none', display: 'flex', alignItems: 'center', zIndex: 1,
        }}>
          <FaMapMarkerAlt size={iconSize} />
        </span>
        <input
          type="text" value={value} onChange={handleChange}
          placeholder={placeholder || label} dir={isRTL ? 'rtl' : 'ltr'}
          style={{
            ...buildDropdownStyle(fs, isRTL),
            paddingLeft:  isRTL ? '10px'               : `${iconSize + 20}px`,
            paddingRight: isRTL ? `${iconSize + 20}px` : '10px',
          }}
        />
        {value && (
          <span onClick={() => { setValue(''); router.push(removeSearchParam(paramKey)); router.refresh(); onFilterChange?.({ filter_name: paramKey, filter_value: null }) }}
            style={{
              position: 'absolute', [isRTL ? 'left' : 'right']: 10,
              top: '50%', transform: 'translateY(-50%)',
              cursor: 'pointer', opacity: 0.4, fontSize: '0.8em',
            }}>✕</span>
        )}
      </div>
    </div>
  )
}

// ─── Name search ──────────────────────────────────────────────────────────────

function NameSearchInput({ label, fs, router, lang, onFilterChange }) {
  const isRTL = RTL_LANGS.has(lang)
  const [value, setValue] = useState(() => getParam('name') || '')
  const handleChange = (e) => {
    const v = e.target.value; setValue(v)
    const path = v ? updateSearchParams('name', v) : removeSearchParam('name')
    router.push(path)
    onFilterChange?.({ filter_name: 'name', filter_value: v || null })
  }
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <FieldLabel label={label} fs={fs} isRTL={isRTL} />
      <input
        type="text" value={value} onChange={handleChange} placeholder={label}
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{ ...buildDropdownStyle(fs, isRTL) }}
      />
    </div>
  )
}

// ─── View Toggle (pill) ───────────────────────────────────────────────────────

function ViewTogglePill({ fs, showMap, onMapToggle, lang, slotLabels }) {
  const isRTL      = RTL_LANGS.has(lang)
  const activeBg   = fs.viewToggleActiveBg     || '#111'
  const activeTc   = fs.viewToggleActiveText   || '#fff'
  const inactiveBg = fs.viewToggleInactiveBg   || '#f3f4f6'
  const inactiveTc = fs.viewToggleInactiveText || '#374151'
  const br         = fs.viewToggleBorderRadius || '6px'
  const h          = fs.height || '38px'
  const items = [
    { icon: <FaList size={13} />,         label: resolveLabel('listView',  slotLabels, lang), isMap: false },
    { icon: <FaMapMarkerAlt size={13} />, label: resolveLabel('mapToggle', slotLabels, lang), isMap: true  },
  ]
  const orderedItems = isRTL ? [...items].reverse() : items
  return (
    <div style={{
      display: 'flex', flexDirection: 'row',
      background: inactiveBg, borderRadius: br,
      padding: 3, gap: 2, height: h, alignItems: 'center', boxSizing: 'border-box',
    }}>
      {orderedItems.map(item => {
        const isActive = showMap === item.isMap
        return (
          <button key={String(item.isMap)} onClick={() => onMapToggle(item.isMap)} style={{
            background:   isActive ? activeBg   : 'transparent',
            color:        isActive ? activeTc   : inactiveTc,
            borderRadius: `calc(${br} - 2px)`, border: 'none',
            padding: '0 12px', height: '100%',
            display: 'flex', alignItems: 'center', flexDirection: 'row',
            gap: 6, fontSize: fs.fontSize || '0.875rem',
            fontWeight: isActive ? 600 : 400,
            cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
          }}>
            {item.icon}{item.label}
          </button>
        )
      })}
    </div>
  )
}

// ─── Property Count display ───────────────────────────────────────────────────

function PropertyCountDisplay({ count, values, lang }) {
  const isRTL  = RTL_LANGS.has(lang)
  const color  = values.propertyCountColor  || '#374151'
  const size   = values.propertyCountSize   || '0.875rem'
  const weight = values.propertyCountWeight || '600'
  const tpl    = resolveCountLabel('propertyCount', values.slotLabels, lang, count)
  return (
    <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', direction: isRTL ? 'rtl' : 'ltr' }}>
      <span style={{ fontSize: size, fontWeight: weight, color }}>{tpl}</span>
    </div>
  )
}

// ─── More Filters Toggle button ───────────────────────────────────────────────

function MoreFiltersButton({ config, fs, lang, hasActiveMoreFilters, onToggle }) {
  const isRTL = RTL_LANGS.has(lang)
  const mb = config.moreBtn || {}
  const btnLabel = mb.label?.[lang] || mb.label?.en || resolveLabel('moreFiltersToggle', config.slotLabels, lang)
  return (
    <button onClick={onToggle} style={{
      position: 'relative',
      background: mb.bg || '#fff', color: mb.textColor || '#374151',
      border: `1px solid ${mb.borderColor || '#d1d5db'}`,
      borderRadius: mb.borderRadius || '6px',
      height: fs.height || '38px', padding: '0 12px',
      display: 'flex', alignItems: 'center',
      flexDirection: isRTL ? 'row-reverse' : 'row',
      gap: 6, cursor: 'pointer', fontWeight: 600,
      fontSize: fs.fontSize || '0.875rem', whiteSpace: 'nowrap',
      direction: isRTL ? 'rtl' : 'ltr',
    }}>
      {hasActiveMoreFilters && (
        <span style={{
          position: 'absolute', top: 3, [isRTL ? 'left' : 'right']: 3,
          width: 7, height: 7, borderRadius: '50%',
          background: mb.activeIndicatorColor || '#3b82f6',
        }}/>
      )}
      {(mb.showIcon ?? true)  && <AiOutlineMenuFold size={15} />}
      {(mb.showLabel ?? true) && btnLabel}
      <span style={{ opacity: 0.5, fontSize: '0.65em' }}>▾</span>
    </button>
  )
}

// ─── Slot Renderer ────────────────────────────────────────────────────────────

function SlotRenderer({
  slot, config, lang, btsList, districtList, provinceList, projectList, amenityList, tagList, showMap,
  searchParams, onMapToggle, onSearch, onReset, onMoreToggle,
  salePriceFilter, setSalePriceFilter, categoryFilter,
  propertyCount, hasActiveMoreFilters, router,
  onFilterChange, filterType,
}) {
  const isRTL = RTL_LANGS.has(lang)
  const fs = config.filterStyle || {}
  const sw = config.switchStyle || {}
  const sb = config.searchBtn   || {}
  const rb = config.resetBtn    || {}
  const sl = config.slotLabels  || {}
  const label = resolveLabel(slot, sl, lang)

  if (slot === 'saleType') {
    const SALE_TYPE_LABELS = {
      sale: { en: 'Sale', th: 'ขาย', cn: '出售', ru: 'Продажа', fr: 'Vente', de: 'Kauf', it: 'Vendita', ja: '売買', ko: '매매', es: 'Venta', he: 'מכירה', ar: 'بيع' },
      rent: { en: 'Rent', th: 'เช่า', cn: '租赁', ru: 'Аренда', fr: 'Location', de: 'Miete', it: 'Affitto', ja: '賃貸', ko: '임대', es: 'Alquiler', he: 'השכרה', ar: 'إيجار' },
    }
    const options = config.saleTypeOptions || [{ value: 'sale' }, { value: 'rent' }]
    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <FieldLabel label={label} fs={fs} isRTL={isRTL} />
        <div style={{
          display: 'flex', flexDirection: isRTL ? 'row-reverse' : 'row',
          direction: isRTL ? 'rtl' : 'ltr',
          background: fs.bg || '#f3f4f6',
          border: `${fs.borderWidth || 1}px solid ${fs.borderColor || '#d1d5db'}`,
          borderRadius: fs.borderRadius || '6px',
          padding: '3px', gap: '3px',
          height: fs.height || '38px', boxSizing: 'border-box', alignItems: 'center',
        }}>
          {options.map(o => {
            const isActive = salePriceFilter === o.value
            const btnLabel = o.labels?.[lang] || o.labels?.en || o.label
              || SALE_TYPE_LABELS[o.value]?.[lang] || SALE_TYPE_LABELS[o.value]?.en || o.value
            return (
              <button key={o.value} onClick={() => {
                setSalePriceFilter(o.value)
                router.push(updateSearchParams('saleType', o.value)); router.refresh()
                onFilterChange?.({ filter_name: 'saleType', filter_value: o.value })
              }} style={{
                flex: 1, height: '100%', border: 'none',
                borderRadius: `calc(${fs.borderRadius || '6px'} - 2px)`,
                background: isActive ? (fs.activeToggleBg || '#ffffff') : 'transparent',
                color: isActive ? (fs.activeToggleColor || '#111') : (fs.textColor || '#6b7280'),
                fontWeight: isActive ? 600 : 400, fontSize: fs.fontSize || '0.875rem',
                cursor: 'pointer', boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}>{btnLabel}</button>
            )
          })}
        </div>
      </div>
    )
  }

  if (slot === 'category') {
    const options = (categoryTypes?.[lang] || []).map(o => ({ value: String(o.value), label: String(o.title || o.label || o.value) }))
    return <SearchableSelect paramKey="category" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'categories') {
    const options = (categoryTypes?.[lang] || []).filter(o => o.value !== 'all').map(o => ({ value: String(o.value), label: String(o.title || o.label || o.value) }))
    return <MultiSelect paramKey="category" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'district') {
    const scoped  = getProvinceScopedDistrictList(districtList, searchParams)
    const options = (scoped || []).map(d => ({ value: String(d.value || d.id || d), label: String(d.label || d.subdistrict || d) }))
    return <SearchableSelect paramKey="district" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'districts') {
    const scoped  = getProvinceScopedDistrictList(districtList, searchParams)
    const options = (scoped || []).map(d => ({ value: String(d.value || d.id || d), label: String(d.label || d.subdistrict || d) }))
    return <MultiSelect paramKey="districts" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'province') {
    const options = (provinceList || [])
      .map(p => ({ value: String(p.value ?? p.id ?? ''), label: String(p.label || p.province || p.value || p.id || '') }))
      .filter(o => o.value !== '')
    return <SearchableSelect paramKey="province" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'provinces') {
    const options = (provinceList || [])
      .map(p => ({ value: String(p.value ?? p.id ?? ''), label: String(p.label || p.province || p.value || p.id || '') }))
      .filter(o => o.value !== '')
    return <MultiSelect paramKey="provinces" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'project') {
    const options = (projectList || []).map(d => ({ value: String(d.value || d.id || d), label: String(d.label || d.name || d) }))
    return <MultiSelect paramKey="project" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'bts') {
    const options = (btsList || []).map(b => ({ value: String(b.id), label: String(b.stationName) }))
    return <SearchableSelect paramKey="bts" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }

  // ── Amenities & Tags — curated (config.amenityOptions/tagOptions) or derived from DB list ──
  // For amenities, curated entries store the amenity's stringified Mongo id as
  // `value` (matching Property.amenities) plus a multilingual `label`, saved
  // via the admin's amenity picker. Tags remain a free-text curated list.
  if (slot === 'amenities') {
    const options = resolveOptionList(config.amenityOptions, amenityList, lang)
    return <MultiSelect paramKey="amenities" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'tags') {
    const options = resolveOptionList(config.tagOptions, tagList, lang)
    return <MultiSelect paramKey="tags" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }

  if (slot === 'bedrooms') {
    const options = (homeRoomTypes?.[lang] || []).map(o => ({ value: String(o.value), label: String(o.title || o.label || o.value) }))
    return <GenericSelect paramKey="room" options={options} label={label} fs={fs} router={router} placeholder={label} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'rooms') {
    const options = (homeRoomTypes?.[lang] || []).filter(o => o.value !== '' && o.value != null).map(o => ({ value: String(o.value), label: String(o.title || o.label || o.value) }))
    return <MultiSelect paramKey="rooms" options={options} label={label} fs={fs} router={router} placeholder={label} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'bathrooms') {
    const options = [{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4+' }]
    return <GenericSelect paramKey="bathroom" options={options} label={label} fs={fs} router={router} placeholder={label} lang={lang} onFilterChange={onFilterChange} />
  }

  // ── Price slots — free-text number input ──────────────────────────────────
  if (slot === 'minPrice') {
    const options = ((salePriceFilter === 'rent' ? rentMinPrices : saleMinPrices)?.[lang] || []).map(o => ({ value: String(o.value), label: String(o.nom || o.label || o.value) }))
    return <FreeTextNumberInput paramKey="minprice" options={options} label={label} fs={fs} router={router} placeholder={label} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'maxPrice') {
    const options = ((salePriceFilter === 'rent' ? rentMaxPrices : saleMaxPrices)?.[lang] || []).map(o => ({ value: String(o.value), label: String(o.nom || o.label || o.value) }))
    return <FreeTextNumberInput paramKey="maxprice" options={options} label={label} fs={fs} router={router} placeholder={label} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'minRentPrice') {
    const options = (rentMinPrices?.[lang] || []).map(o => ({ value: String(o.value), label: String(o.nom || o.label || o.value) }))
    return <FreeTextNumberInput paramKey="minprice" options={options} label={label} fs={fs} router={router} placeholder={label} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'maxRentPrice') {
    const options = (rentMaxPrices?.[lang] || []).map(o => ({ value: String(o.value), label: String(o.nom || o.label || o.value) }))
    return <FreeTextNumberInput paramKey="maxprice" options={options} label={label} fs={fs} router={router} placeholder={label} lang={lang} onFilterChange={onFilterChange} />
  }

  // ── Living area slots ─────────────────────────────────────────────────────
  if (slot === 'minLivingArea') {
    return <FreeTextNumberInput paramKey="minlivingarea" label={label} fs={fs} router={router} placeholder={label} lang={lang} unit="m²" onFilterChange={onFilterChange} />
  }
  if (slot === 'maxLivingArea') {
    return <FreeTextNumberInput paramKey="maxlivingarea" label={label} fs={fs} router={router} placeholder={label} lang={lang} unit="m²" onFilterChange={onFilterChange} />
  }

  if (slot === 'sort') {
    const options = [
      { value: 'datedesc', label: 'Newest First' }, { value: 'dateasc', label: 'Oldest First' },
      { value: 'priceasc', label: 'Price: Low → High' }, { value: 'pricedesc', label: 'Price: High → Low' },
    ]
    return <GenericSelect paramKey="sort" options={options} label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  }
  if (slot === 'nameSearch')     return <NameSearchInput label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'locationSearch') return <LocationInput paramKey="location" label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'areaSearch')     return <LocationInput paramKey="area"     label={label} fs={fs} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'propertyCount')  return <PropertyCountDisplay count={propertyCount} values={config} lang={lang} />
  if (slot === 'viewToggle')     return <ViewTogglePill fs={fs} showMap={showMap} onMapToggle={onMapToggle} lang={lang} slotLabels={sl} />
  if (slot === 'moreFiltersToggle') return <MoreFiltersButton config={config} fs={fs} lang={lang} hasActiveMoreFilters={hasActiveMoreFilters} onToggle={onMoreToggle} />

  if (slot === 'petFriendly')   return <ToggleSwitch paramKey="petfriendly"   label={label} sw={sw} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'seaView')       return <ToggleSwitch paramKey="seaview"       label={label} sw={sw} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'beachfront')    return <ToggleSwitch paramKey="beachfront"    label={label} sw={sw} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'pool')          return <ToggleSwitch paramKey="pool"          label={label} sw={sw} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'gym')           return <ToggleSwitch paramKey="gym"           label={label} sw={sw} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'parking')       return <ToggleSwitch paramKey="parking"       label={label} sw={sw} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'furnished')     return <ToggleSwitch paramKey="furnished"     label={label} sw={sw} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'foreignQuota')  return <ToggleSwitch paramKey="foreignquota"  label={label} sw={sw} router={router} lang={lang} onFilterChange={onFilterChange} />
  if (slot === 'mountainView')  return <ToggleSwitch paramKey="mountainview"  label={label} sw={sw} router={router} lang={lang} onFilterChange={onFilterChange} />

  if (slot === 'mapToggle') {
    return (
      <button onClick={() => onMapToggle(!showMap)} style={{
        background: fs.bg || '#fff', color: fs.textColor || '#111',
        border: `${fs.borderWidth || 1}px solid ${fs.borderColor || '#d1d5db'}`,
        borderRadius: fs.borderRadius || '6px',
        height: fs.height || '38px', padding: '0 12px',
        display: 'flex', alignItems: 'center',
        flexDirection: isRTL ? 'row-reverse' : 'row',
        gap: 6, fontSize: fs.fontSize || '0.875rem',
        cursor: 'pointer', whiteSpace: 'nowrap', direction: isRTL ? 'rtl' : 'ltr',
      }}>
        {showMap ? <FaList size={14} /> : <FaMapMarkerAlt size={14} />}
        {showMap ? resolveLabel('listView', sl, lang) : label}
      </button>
    )
  }

  if (slot === 'searchButton') {
    const btnLabel = sb.label?.[lang] || sb.label?.en || resolveLabel('searchButton', sl, lang)
    return (
      <button onClick={() => {
        onSearch()
        fireImmediateGAEvent(`${filterType}_search_clicked`, readCurrentFilters())
      }} style={{
        background: sb.bg || '#111', color: sb.textColor || '#fff',
        borderRadius: sb.borderRadius || '6px',
        height: fs.height || '38px', padding: '0 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: isRTL ? 'row-reverse' : 'row',
        gap: 6, fontWeight: 600, fontSize: fs.fontSize || '0.875rem',
        cursor: 'pointer', border: 'none',
        flex: sb.fullWidth ? 1 : undefined, whiteSpace: 'nowrap',
        direction: isRTL ? 'rtl' : 'ltr',
      }}>
        {(sb.showIcon  ?? true) && '🔍'}
        {(sb.showLabel ?? true) && btnLabel}
      </button>
    )
  }

  if (slot === 'resetButton') {
    const btnLabel = rb.label?.[lang] || rb.label?.en || resolveLabel('resetButton', sl, lang)
    return (
      <button onClick={() => {
        onReset()
        fireImmediateGAEvent(`${filterType}_search_reset`, {})
      }} style={{
        background: rb.bg || '#ef4444', color: rb.textColor || '#fff',
        borderRadius: rb.borderRadius || '6px',
        height: fs.height || '38px', padding: '0 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: isRTL ? 'row-reverse' : 'row',
        gap: 6, fontWeight: 600, fontSize: fs.fontSize || '0.875rem',
        cursor: 'pointer', border: 'none',
        flex: rb.fullWidth ? 1 : undefined, whiteSpace: 'nowrap',
        direction: isRTL ? 'rtl' : 'ltr',
      }}>
        {(rb.showIcon  ?? false) && '↺'}
        {(rb.showLabel ?? true)  && btnLabel}
      </button>
    )
  }

  return null
}

// ─── Column / Row layout renderer ────────────────────────────────────────────

function ColumnsLayout({ columns, isMobile, config, columnGap, rowGap, slotProps }) {
  const isRTL = RTL_LANGS.has(slotProps.lang)
  return (
    <>
      {columns.map((col, ci) => (
        <div key={col.id || ci} style={{
          display: 'flex', flexDirection: 'column', gap: `${rowGap}px`,
          alignItems: col.justify || 'flex-start',
          flex: isMobile ? '1 1 100%' : (col.widthPercent ? `0 0 ${col.widthPercent}%` : '1'),
          width: isMobile ? '100%' : undefined, minWidth: 0,
        }}>
          {(col.rows || []).map((row, ri) => (
            <div key={row.id || ri} style={{
              display: 'flex', flexDirection: 'row',
              gap: `${row.gap || 8}px`,
              flexWrap: row.wrap ? 'wrap' : 'nowrap',
              alignItems: row.align || 'center',
              width: '100%', direction: isRTL ? 'rtl' : 'ltr',
            }}>
              {(row.slots || []).map((slot, si) => (
                <SlotRenderer key={`${slot}-${si}`} slot={slot} {...slotProps} />
              ))}
            </div>
          ))}
        </div>
      ))}
    </>
  )
}

// ─── useIsMobile ──────────────────────────────────────────────────────────────

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [breakpoint])
  return isMobile
}

// ─── Main component ───────────────────────────────────────────────────────────

const PropertySearch = ({ searchFilter, searchParams, btsList, districtList, provinceList, projectList, amenityList, tagList, lang, dictionary, propertyCount, filterType = 'property' }) => {
  const isRTL   = RTL_LANGS.has(lang)
  const router    = useRouter()
  const filterRef = useRef(null)
  const config    = searchFilter || {}
  const columns   = config.columns     || []
  const moreColumns = config.moreColumns || []
  const isMobile  = useIsMobile(config.mobileBreakpoint ?? 640)

  const [salePriceFilter, setSalePriceFilter] = useState(searchParams?.saleType || 'sale')
  const [categoryFilter,  setCategoryFilter]  = useState(searchParams?.category)
  const [showMap,         setShowMap]         = useState(searchParams?.map === 'true' || searchParams?.map === true)
  const [filterOn,        setFilterOn]        = useState(false)
  const [isOpen,          setIsOpen]          = useState(config.displayMode !== 'button-triggered')
  const [moreOpen,        setMoreOpen]        = useState(false)

  // ─── GA4 debounce ref ───────────────────────────────────────────────────────
  const gaDebounceRef = useRef(null)

  const handleFilterChange = ({ filter_name, filter_value }) => {
    if (gaDebounceRef.current) clearTimeout(gaDebounceRef.current)
    gaDebounceRef.current = setTimeout(() => {
      fireImmediateGAEvent(`${filterType}_filter_changed`, {
        filter_name,
        filter_value,
        ...readCurrentFilters(),
      })
    }, 1000)
  }

  useEffect(() => {
    return () => { if (gaDebounceRef.current) clearTimeout(gaDebounceRef.current) }
  }, [])

  const hasActiveMoreFilters = !!(
    searchParams?.district || searchParams?.bts || searchParams?.petfriendly ||
    searchParams?.seaview  || searchParams?.beachfront || searchParams?.foreignquota ||
    searchParams?.mountainview || searchParams?.amenities || searchParams?.tags ||
    searchParams?.province || searchParams?.provinces
  )

  useEffect(() => {
    const sp = searchParams || {}
    setSalePriceFilter(sp.saleType || 'sale')
    setCategoryFilter(sp.category)
    setShowMap(sp.map === 'true' || sp.map === true)
    setFilterOn(
      sp.category    !== undefined || sp.room      !== undefined || sp.rooms    !== undefined ||
      sp.minprice    !== undefined || sp.maxprice  !== undefined || sp.district !== undefined ||
      sp.districts   !== undefined || sp.project   !== undefined || sp.bts      !== undefined ||
      sp.location    !== undefined || sp.area      !== undefined ||
      sp.minlivingarea     !== undefined || sp.maxlivingarea   !== undefined ||
      sp.amenities   !== undefined || sp.tags      !== undefined ||
      sp.province    !== undefined || sp.provinces !== undefined
    )
    if (config.displayMode !== 'button-triggered') setIsOpen(true)
  }, [searchParams])

  useEffect(() => { if (searchParams?.filtertab === 'true') setIsOpen(true) }, [])

  useEffect(() => {
    if (!filterRef.current) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && filterRef.current) {
        filterRef.current.style.position = 'sticky'
        filterRef.current.style.top = '0'
      }
    }, { root: null, threshold: 0 })
    observer.observe(filterRef.current)
    return () => observer.disconnect()
  }, [])

  const handleMapToggle = (next) => {
    setShowMap(next)
    router.push(next ? updateSearchParams('map', 'true') : removeSearchParam('map'))
    router.refresh()
  }
  const handleSearch = () => { setIsOpen(false); setMoreOpen(false); router.refresh() }
  const handleReset  = () => removeParams()

  const columnGap = parseInt(config.columnGap) || 16
  const rowGap    = parseInt(config.rowGap)    || 8

  const slotProps = {
    config, lang, btsList, districtList, provinceList, projectList, amenityList, tagList, searchParams,
    showMap, salePriceFilter, setSalePriceFilter, categoryFilter,
    propertyCount, hasActiveMoreFilters,
    onMapToggle: handleMapToggle, onSearch: handleSearch,
    onReset: handleReset, onMoreToggle: () => setMoreOpen(o => !o),
    router,
    onFilterChange: handleFilterChange,
    filterType,
  }

  const panelStyle = {
    background:   config.panelBg           || '#ffffff',
    border:       `1px solid ${config.panelBorderColor || '#e5e7eb'}`,
    borderRadius: config.panelBorderRadius  || '8px',
    boxShadow:    config.panelBoxShadow     || 'none',
    padding:      `${config.panelPaddingY || 16}px ${config.panelPaddingX || 16}px`,
    direction:    isRTL ? 'rtl' : 'ltr',
  }

  const morePanelStyle = {
    background:   config.moreFiltersPanelBg     || '#ffffff',
    border:       `1px solid ${config.moreFiltersPanelBorder || '#e5e7eb'}`,
    borderRadius: config.moreFiltersPanelRadius  || '8px',
    boxShadow:    config.moreFiltersPanelShadow  || '0 8px 32px rgba(0,0,0,0.15)',
    padding:      `${config.moreFiltersPanelPaddingY || 16}px ${config.moreFiltersPanelPaddingX || 16}px`,
    direction:    isRTL ? 'rtl' : 'ltr',
  }

  const moreMode = config.moreFiltersPanelMode || 'dropdown'

  const panelInner = (
    <div style={{
      ...panelStyle,
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: `${isMobile ? rowGap : columnGap}px`,
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      alignItems: 'flex-start',
    }}>
      <ColumnsLayout columns={columns} isMobile={isMobile} config={config} columnGap={columnGap} rowGap={rowGap} slotProps={slotProps} />
    </div>
  )

  const morePanelContent = moreColumns.length > 0 && moreOpen && (
    <div style={{
      ...morePanelStyle,
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: `${isMobile ? rowGap : columnGap}px`,
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      alignItems: 'flex-start',
    }}>
      <ColumnsLayout columns={moreColumns} isMobile={isMobile} config={config} columnGap={columnGap} rowGap={rowGap} slotProps={slotProps} />
    </div>
  )

  const isButtonTriggered = config.displayMode === 'button-triggered'
  const triggerLabel = config.triggerLabel?.[lang] || config.triggerLabel?.en || 'Filter'

  const drawerPanel = (onClose) => (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 299 }} />
      <div style={{
        position: 'fixed', top: 0, [isRTL ? 'left' : 'right']: 0, bottom: 0, zIndex: 300,
        width: Math.min(400, typeof window !== 'undefined' ? window.innerWidth : 400),
        overflowY: 'auto',
        ...morePanelStyle,
        borderRadius: isRTL ? '0 12px 12px 0' : '12px 0 0 12px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <span style={{ fontWeight: 700, fontSize: '1rem' }}>{resolveLabel('moreFiltersToggle', config.slotLabels, lang)}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#6b7280' }}>✕</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: `${rowGap}px` }}>
          <ColumnsLayout columns={moreColumns} isMobile={true} config={config} columnGap={columnGap} rowGap={rowGap} slotProps={slotProps} />
        </div>
      </div>
    </>
  )

  if (!isButtonTriggered) {
    return (
      <div className='container-wide mx-auto' ref={filterRef} style={{ zIndex: 200, position: 'relative', direction: isRTL ? 'rtl' : 'ltr' }}>
        {panelInner}
        {moreColumns.length > 0 && moreMode === 'inline'   && moreOpen && <div style={{ marginTop: 4 }}>{morePanelContent}</div>}
        {moreColumns.length > 0 && moreMode === 'dropdown' && moreOpen && <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 201, marginTop: 4 }}>{morePanelContent}</div>}
        {moreColumns.length > 0 && moreMode === 'drawer'   && moreOpen && drawerPanel(() => setMoreOpen(false))}
      </div>
    )
  }

  return (
    <div className='container-wide mx-auto' ref={filterRef} style={{ zIndex: 200, position: 'relative', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{
        display: 'flex', justifyContent: 'flex-end', flexDirection: 'row',
        alignItems: 'center', padding: '6px 12px',
        borderBottom: `1px solid ${config.panelBorderColor || '#e5e7eb'}`,
        background: config.panelBg || '#fff', gap: '6px',
      }}>
        <button onClick={() => handleMapToggle(!showMap)} style={{
          background: config.filterStyle?.bg || '#fff', color: config.filterStyle?.textColor || '#111',
          border: `1px solid ${config.filterStyle?.borderColor || '#d1d5db'}`,
          borderRadius: config.filterStyle?.borderRadius || '6px', padding: '6px 12px',
          display: 'flex', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row',
          gap: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
        }}>
          {showMap ? <FaList size={14} /> : <FaMapMarkerAlt size={14} />}
          {showMap ? resolveLabel('listView', config.slotLabels, lang) : resolveLabel('mapToggle', config.slotLabels, lang)}
        </button>
        <button onClick={() => setIsOpen(o => !o)} style={{
          position: 'relative',
          background: config.triggerBg || '#ffffff', color: config.triggerTextColor || '#111111',
          border: `1px solid ${config.triggerBorderColor || '#d1d5db'}`,
          borderRadius: config.triggerBorderRadius || '6px', padding: '6px 14px',
          display: 'flex', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row',
          gap: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
        }}>
          {(config.showFilterIndicator && filterOn) && (
            <span style={{ position: 'absolute', top: 3, [isRTL ? 'left' : 'right']: 3, width: 8, height: 8, borderRadius: '50%', background: '#3b82f6' }} />
          )}
          {(config.triggerShowIcon  ?? true) && <AiOutlineMenuFold size={16} />}
          {(config.triggerShowLabel ?? true) && triggerLabel}
        </button>
      </div>
      {isOpen && <div onClick={() => { setIsOpen(false); setMoreOpen(false) }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 199 }} />}
      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 201 }}>
          {panelInner}
          {moreColumns.length > 0 && moreMode !== 'drawer' && moreOpen && <div style={{ marginTop: 4 }}>{morePanelContent}</div>}
        </div>
      )}
      {moreColumns.length > 0 && moreMode === 'drawer' && moreOpen && drawerPanel(() => setMoreOpen(false))}
    </div>
  )
}

export default PropertySearch